import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/ContactForm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollProgress, ScrollToTop } from '@/components/ScrollAnimations';
import AnimatedPageHero from '@/components/AnimatedPageHero';
import { Helmet } from 'react-helmet-async';
import { services } from '@/data/siteData';
import { 
  Monitor, 
  Code, 
  Cloud, 
  Shield,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

const iconMap = {
  Monitor,
  Code,
  Cloud,
  Shield,
};

const servicesList = Object.values(services);

export const ServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Our Services - Felix Infotech | Compute, Network, Monitoring & Professional Services</title>
        <meta name="description" content="Explore Felix Infotech's comprehensive IT services: Compute & Infrastructure, Network & Security, SOC Monitoring, and Professional Services. 22+ years of expertise across India, Dubai & Singapore." />
        <meta name="keywords" content="IT services India, compute solutions, network security services, SOC monitoring, managed IT services, servers, storage, firewall, VAPT, cybersecurity Chennai" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://felixinfotech.com/services" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="IT Services - Felix Infotech | Comprehensive Technology Solutions" />
        <meta property="og:description" content="End-to-end IT services: Compute, Network & Security, Monitoring, and Professional Services with 22+ years of expertise." />
        <meta property="og:url" content="https://felixinfotech.com/services" />
        <meta property="og:site_name" content="Felix Infotech" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="IT Services by Felix Infotech" />
        <meta name="twitter:description" content="Compute, Network & Security, Monitoring, and Professional Services with 22+ years of expertise." />
      </Helmet>

      <ScrollProgress />
      <Navbar />

      <main>
        {/* Animated Hero Section */}
        <AnimatedPageHero
          title="Comprehensive Technology Solutions"
          subtitle="End-to-End IT Services"
          description="From infrastructure to security, we deliver tailored solutions that drive your business forward in the digital age."
          badge="Our Services"
          animationType="ict"
          gradient="from-primary to-accent"
          breadcrumbs={[{ label: 'Services' }]}
        />

        {/* Services Overview */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-6">
            <div className="grid lg:grid-cols-2 gap-8">
              {servicesList.map((service, index) => {
                const IconComponent = iconMap[service.icon] || Monitor;
                return (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link to={`/services/${service.id}`}>
                      <div className="group relative bg-card rounded-3xl overflow-hidden shadow-soft border border-border hover:shadow-elevated transition-all hover:-translate-y-2">
                        {/* Image */}
                        <div className="relative h-64">
                          <img
                            src={service.image}
                            alt={service.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 to-transparent" />
                          
                          {/* Icon Badge */}
                          <div className={`absolute top-6 left-6 w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center shadow-lg`}>
                            <IconComponent className="w-7 h-7 text-white" />
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-8">
                          <p className={`text-sm font-medium bg-gradient-to-r ${service.gradient} bg-clip-text text-transparent mb-2`}>
                            {service.subtitle}
                          </p>
                          <h3 className="font-display text-2xl font-bold text-foreground mb-3">
                            {service.title}
                          </h3>
                          <p className="text-muted-foreground mb-6">
                            {service.description}
                          </p>

                          {/* Sub-services preview */}
                          <div className="flex flex-wrap gap-2 mb-6">
                            {service.subServices.slice(0, 4).map((sub, idx) => (
                              <span
                                key={idx}
                                className="px-3 py-1 rounded-full bg-muted text-xs font-medium text-muted-foreground"
                              >
                                {sub.title}
                              </span>
                            ))}
                            {service.subServices.length > 4 && (
                              <span className="px-3 py-1 rounded-full bg-primary/10 text-xs font-medium text-primary">
                                +{service.subServices.length - 4} more
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-2 text-primary font-medium group-hover:gap-4 transition-all">
                            Explore Services
                            <ArrowRight className="w-5 h-5" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why Choose Our Services */}
        <section className="py-20 bg-muted">
          <div className="container mx-auto px-4 lg:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground section-title">
                Why Choose Our Services?
              </h2>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: '22+ Years Experience', description: 'Proven expertise in delivering technology solutions' },
                { title: '100+ Happy Clients', description: 'Trusted by businesses across industries' },
                { title: '24/7 Support', description: 'Round-the-clock assistance for critical issues' },
                { title: 'ISO Certified', description: 'Quality and security assured processes' },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-card rounded-2xl p-6 shadow-soft border border-border text-center"
                >
                  <CheckCircle2 className="w-10 h-10 text-primary mx-auto mb-4" />
                  <h3 className="font-display font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <ContactForm />
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default ServicesPage;
